import React, { useEffect } from 'react'

import { Box, Container, Grid, useTheme } from '@material-ui/core'
import { observer } from 'mobx-react'
import { useSnackbar } from 'notistack'
import { Helmet } from 'react-helmet'

import { ScrollResetter } from 'shared/components/atoms'
import {
  MARKETPLACE_CODE_PRINT_ON_DEMAND,
  MARKETPLACE_CODE_READY_TO_SELL,
} from 'shared/constants/marketplace'
import { useTranslation } from 'shared/hooks'

import { PageHeader } from 'retailer/components/molecules'
import {
  UniversalNotifications,
  OnboardingWidget,
  SupplierMarketplaceButtons,
  ProductCarouselCard,
  MarketplaceBanners,
  SupplierMarketplaceNavigationModal,
  CtaCard,
} from 'retailer/components/organisms'
import { SidebarLeftTemplate } from 'retailer/components/templates'
import { useStores } from 'retailer/stores/hooks'

import podImage from './print-on-demand.jpg'
import rtsImage from './ready-to-sell.jpg'

const DashboardPage = () => {
  const { t } = useTranslation('mainDashboard', { keyPrefix: 'modalyst.dashboard' })
  const { dashboardStore, userProfileStore, connectToSuppliersStore, routerStore } = useStores()
  const { podCarouselStore, rtsCarouselStore } = dashboardStore
  const supplierMarketplaceNavigationRequest = connectToSuppliersStore.marketplaceNavigationRequest
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    const connected = routerStore.getSearchParamValue('connected')
    if (connected) {
      enqueueSnackbar('Your store has been successfully connected', { variant: 'success' })
      routerStore.replace('/home')
    }
  }, [])

  return (
    <SidebarLeftTemplate
      notifications={<UniversalNotifications />}
      main={
        <Container maxWidth="xl" disableGutters>
          <Helmet>
            <title>{t('findProducts.mainTitle')}</title>
          </Helmet>
          <ScrollResetter />
          <PageHeader
            title={t('findProducts.mainTitle')}
            subtitle={t('findProducts.subtitle')}
            mb={3}
          />
          {userProfileStore.onboarding && (
            <Box>
              <OnboardingWidget
                elevation={0}
                store={userProfileStore.onboarding}
                onDismiss={() => userProfileStore.dismissOnboarding()}
              />
            </Box>
          )}

          <Box
            my={3} overflow="hidden"
            bgcolor={theme.palette.background.paper}
            borderRadius={theme.shape.borderRadius}
          >
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <CtaCard
                  square backgroundColor="#ee9da4"
                  image={podImage}
                  primary={t('podBanner.title')}
                  secondary={t('podBanner.button')}
                  to={`/marketplace/${MARKETPLACE_CODE_PRINT_ON_DEMAND}`}
                >
                  {t('podBanner.subtitle')}
                </CtaCard>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CtaCard
                  square backgroundColor="#ffaa01"
                  image={rtsImage}
                  primary={t('readyBanner.title')}
                  secondary={t('readyBanner.button')}
                  to={`/marketplace/${MARKETPLACE_CODE_READY_TO_SELL}`}
                >
                  {t('readyBanner.subtitle')}
                </CtaCard>
              </Grid>
            </Grid>
          </Box>
          <SupplierMarketplaceButtons my={3} elevation={0} />
          <Box
            my={3}
            style={{ display: 'grid', gridAutoRows: '1fr', gridRowGap: theme.spacing(3) }}
          >
            <ProductCarouselCard
              elevation={0}
              minHeight={{ lg: theme.spacing(20) }}
              primary={t('readyEntry.title')}
              secondary={t('readyEntry.subtitle')}
              buttonText={t('readyEntry.button')}
              to={`/marketplace/${MARKETPLACE_CODE_READY_TO_SELL}`}
              background="#A66F01"
              productCarouselStore={rtsCarouselStore}
            />
            <ProductCarouselCard
              elevation={0}
              minHeight={{ lg: theme.spacing(20) }}
              primary={t('podEntry.title')}
              secondary={t('podEntry.subtitle')}
              buttonText={t('podEntry.button')}
              to={`/marketplace/${MARKETPLACE_CODE_PRINT_ON_DEMAND}`}
              background="#9B666B"
              productCarouselStore={podCarouselStore}
            />
          </Box>
          <MarketplaceBanners />
          {supplierMarketplaceNavigationRequest && (
            <SupplierMarketplaceNavigationModal
              store={supplierMarketplaceNavigationRequest}
              onExited={() => connectToSuppliersStore.finishMarketplaceNavigation()}
            />
          )}
        </Container>
      }
    />
  )
}

export default observer(DashboardPage)
